import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    Config,
    Select,
    Products,
    PROVINCE,
    useIsMounted,
    LegacyCityPropType,
    MessagePropType,
    getAffiliateData,
    LocationSelector,
    getLanguageFromLocale,
    AnimatedExpandingContainer,
    AFFILIATE_QUERY_PARAM_KEYS,
} from '@ratehub/base-ui';
import {
    SAVINGS_TYPES,
    SAVINGS_TYPES_OPTIONS,
    SAVINGS_MONTHLY_BALANCES,
    SAVINGS_MONTHLY_BALANCES_OPTIONS,
} from '@ratehub/inv-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import SavingsAccountPropType from '../definitions/SavingsAccountPropType';
import getSavingsAccountProps from '../functions/getSavingsAccountProps';
import fetchProductsForSavings from '../functions/fetchProductsForSavings';
import useTrackSponsoredProductsHeapEvent from '../hooks/useTrackSponsoredProductsHeapEvent';
import useSavingsInterestCalculationCallback from '../hooks/useSavingsInterestCalculationCallback';
import ProductList from './ProductList';
import ProductCardWithBigNumberBox from './ProductCardWithBigNumberBox';


function ProductListSavings({
    title,

    initialAccountType = SAVINGS_TYPES.ALL,
    initialMonthlyBalance = SAVINGS_MONTHLY_BALANCES[0],     // $1,000
    initialProvider,

    initialCity,

    initialProducts = [],
    lastUpdated,

    maxRowsCollapsed,
    maxRowsExpanded,

    ...otherProps
}) {
    const intl = useIntl();
    const getIsMounted = useIsMounted();
    const showCalculationModalCallback = useSavingsInterestCalculationCallback();

    // TODO: remove workaround after LocationSelector fixed for FilteredAsyncContainer
    // see https://ratehub.atlassian.net/browse/CCDEP-3541
    const [ provinceCode, setProvinceCode ] = useState(initialCity?.province.code || PROVINCE.ONTARIO);
    function updateLocation(values) {
        if (values?.province?.code) {
            setProvinceCode(values.province.code);
        }
    }

    const [ pausingOnFirstLoad, setPausingOnFirstLoad ] = useState(true);
    // provide a pause for ProductList to sort themselves out and for
    // the list to update itself from the products it was exported with
    useEffect(() => {
        if (!pausingOnFirstLoad) {
            return;
        }

        new Promise((resolve) => setTimeout(resolve, 400))
            .then(() => {
                if (getIsMounted()) {
                    setPausingOnFirstLoad(false);
                }
            });
    }, []);

    const [ lastUpdatedDate, setLastUpdatedDate ] = useState(lastUpdated);

    // used to track sponsored savings accounts onload
    useTrackSponsoredProductsHeapEvent(initialProducts, Products.SAVINGS);

    const language = getLanguageFromLocale(intl.locale);

    const initialFilterValues = {
        accountType: initialAccountType,
        monthlyBalance: initialMonthlyBalance,
        provider: initialProvider,
        provinceCode: provinceCode,
        language: language,
    };

    function getFormValuesForFetch(values) {
        return {
            ...initialFilterValues,
            accountType: values.accountType,
            monthlyBalance: values.monthlyBalance,
            transactions: values.transactions,
            provinceCode: values.location?.province.code,
        };
    }

    async function fetchProductSavings(values) {
        const savingsParams = {
            ...values,
            isPrioritizeSponsored: true,
            customId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
        };

        const response = await fetchProductsForSavings(savingsParams);
        if (response) {
            if (getIsMounted()) {
                setLastUpdatedDate(response.lastUpdated);
            }
        }

        return response.products;
    }


    return (
        <AnimatedExpandingContainer
            alwaysRenderChildMarkup
            isVisible={!pausingOnFirstLoad}
            className="rh-flex-grow-1"
        >
            <ProductList
                {...otherProps}
                title={title}
                lastUpdated={lastUpdatedDate}

                maxRowsCollapsed={maxRowsCollapsed}
                maxRowsExpanded={maxRowsExpanded}
                noMatchingItemsMessage={(
                    <FormattedMessage
                        id="web-components.edb.savings.product-list.no-matches.label"
                        defaultMessage="No saving accounts match the search filters."
                    />
                )}

                initialFilterValues={initialFilterValues}
                primaryFilters={getSavingsFilters({
                    initialAccountType,
                    initialMonthlyBalance,
                    initialCity,
                    updateLocation,
                })}
                initialFetchedProducts={initialProducts}
                getFormValues={getFormValuesForFetch}
                onFetchValue={fetchProductSavings}
            >
                {(savingsAccount, formValues) => (
                    <ProductCardWithBigNumberBox
                        product={getSavingsAccountProps({
                            savingsAccount,
                            monthlyBalance: formValues.monthlyBalance,
                            provinceCode: formValues.provinceCode,
                            bigNumberClick: () => showCalculationModalCallback({
                                savingsAccount,
                                monthlyBalance: formValues.monthlyBalance,
                            }),
                        })}
                        hasUniformPadding={false}
                    />
                )}
            </ProductList>
        </AnimatedExpandingContainer>
    );
}


function getSavingsFilters({
    initialAccountType,
    initialMonthlyBalance,
    initialCity,
    updateLocation,
}) {
    return [
        <Select
            key="accountType"
            name="accountType"
            value={initialAccountType}
            options={Config.ENABLE_FHSA
                ? SAVINGS_TYPES_OPTIONS
                : SAVINGS_TYPES_OPTIONS.filter(option => option.value !== SAVINGS_TYPES.FHSA)
            }
            label={(
                <FormattedMessage
                    id="web-components.edb.savings.product-list.filters.accountType.label"
                    defaultMessage="Type"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <Select
            key="monthlyBalance"
            name="monthlyBalance"
            value={initialMonthlyBalance}
            options={SAVINGS_MONTHLY_BALANCES_OPTIONS}
            label={(
                <FormattedMessage
                    id="web-components.edb.savings.product-list.filters.balance.label"
                    defaultMessage="Balance"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <LocationSelector
            name="location"
            key="location"
            initialProvince={initialCity?.province.code}
            label={(
                <FormattedMessage
                    id="web-components.edb.savings.product-list.filters.location.label"
                    defaultMessage="Location"
                />
            )}
            className="filter-question-compact"
            errorMessageStyle="minimal"
            animationType="none"
            onChange={updateLocation}
        />,
    ];
}




ProductListSavings.propTypes = {
    title: MessagePropType.isRequired,

    initialAccountType: PropTypes.oneOf(Object.values(SAVINGS_TYPES)),
    initialMonthlyBalance: PropTypes.oneOf(SAVINGS_MONTHLY_BALANCES),
    initialProvider: PropTypes.string,

    initialCity: LegacyCityPropType,

    initialProducts: PropTypes.arrayOf(SavingsAccountPropType),
    lastUpdated: PropTypes.string,

    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,
};


ProductListSavings.blockKey = 'rh/product-list-savings';
ProductListSavings.requiresLayoutRow = true;
ProductListSavings.supportedPageTypes = [ PAGE_TEMPLATES.savings ];

export default observer(ProductListSavings);
